import { useState } from 'react';
import { createPortal } from 'react-dom';
import { formatDate, formatAmount, getCategoryIcon } from '../utils/formatters';
import { T, amountStyle } from '../theme';
import { EXPENSE_REFUND_CLASS_KEYS, resolveClassification } from '../utils/constants';
import { Link2, Trash2 } from 'lucide-react';

// function isPdf(expense) {
//   if (expense.mimeType === 'application/pdf') return true;
//   if (expense.imagePreview?.startsWith('data:application/pdf')) return true;
//   return false;
// }

// function openInNewTab(e, url) {
//   e.stopPropagation();
//   if (!url) return;
//   // Convert base64 data URLs to blob URLs so the browser opens them properly
//   if (url.startsWith('data:')) {
//     const [header, data] = url.split(',');
//     const mime = header.match(/:(.*?);/)[1];
//     const bytes = atob(data);
//     const ab = new Uint8Array(bytes.length);
//     for (let i = 0; i < bytes.length; i++) ab[i] = bytes.charCodeAt(i);
//     const blob = new Blob([ab], { type: mime });
//     const blobUrl = URL.createObjectURL(blob);
//     window.open(blobUrl, '_blank');
//   } else {
//     window.open(url, '_blank');
//   }
// }

// function ReceiptThumbnail({ expense }) {
//   const [imgError, setImgError] = useState(false);
//   const preview = expense.imagePreview;

//   if (!preview) return null;

//   const pdf = isPdf(expense) || imgError;

//   if (pdf) {
//     return (
//       <button
//         onClick={(e) => openInNewTab(e, preview)}
//         style={{
//           display: 'flex',
//           alignItems: 'center',
//           gap: 6,
//           marginBottom: 6,
//           padding: '6px 10px',
//           borderRadius: 8,
//           border: '1.5px solid #E5E7EB',
//           background: '#F9FAFB',
//           cursor: 'pointer',
//           color: '#667eea',
//           fontSize: 12,
//           fontWeight: 700,
//         }}
//       >
//         📄 View PDF
//       </button>
//     );
//   }

//   return (
//     <div style={{ marginBottom: 6 }}>
//       <img
//         src={preview}
//         alt="Receipt"
//         onError={() => setImgError(true)}
//         onClick={(e) => openInNewTab(e, preview)}
//         style={{
//           width: '100%',
//           height: 100,
//           objectFit: 'cover',
//           borderRadius: 8,
//           border: '1px solid #E5E7EB',
//           cursor: 'zoom-in',
//           display: 'block',
//         }}
//       />
//     </div>
//   );
// }

function DeleteExpenseModal({ vendorName, onConfirm, onCancel }) {
  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '0 24px' }}
      onClick={onCancel}
    >
      <div
        style={{ background: '#fff', borderRadius: 16, padding: '24px 24px 20px', width: '100%', maxWidth: 340, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ width: 48, height: 48, borderRadius: 14, background: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
          <Trash2 size={22} color="#DC2626" />
        </div>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#111', marginBottom: 6 }}>Delete expense?</div>
        <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>
          <span style={{ fontWeight: 700, color: '#374151' }}>"{vendorName}"</span> will be permanently deleted.
        </div>
        <div style={{ fontSize: 12, color: '#DC2626', fontWeight: 600, marginBottom: 20 }}>
          This cannot be undone.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onCancel}
            style={{ flex: 1, padding: '11px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', color: '#6B7280' }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{ flex: 1, padding: '11px', borderRadius: 10, border: 'none', background: '#DC2626', fontSize: 14, fontWeight: 700, cursor: 'pointer', color: '#fff' }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ExpenseItem({ expense, onClick, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const IconComponent = getCategoryIcon(expense.category);
  const isCredit = expense.transactionDirection === 'credit';
  const resolvedCls = resolveClassification(expense.businessClassification, expense.isRefund, expense.transactionDirection);
  const isRefund = EXPENSE_REFUND_CLASS_KEYS.has(resolvedCls);
  const isPaired = !!(expense.pairedWithId && expense.pairedWithFile);

  return (
    <div
      onClick={() => onClick(expense)}
      style={{
        background: "#fff",
        borderRadius: 16,
        marginBottom: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        cursor: "pointer",
        transition: "all 0.2s",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        // Left bar: green for credit, invisible for debit
        borderLeft: isCredit ? "4px solid #16a34a" : "4px solid transparent",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)";
      }}
    >
      {/* Inner wrapper keeps padding consistent regardless of border */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px", flex: 1, minWidth: 0 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: isCredit ? "#DCFCE7" : T.tealPale,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <IconComponent size={22} color={isCredit ? "#15803d" : T.tealDeep} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 15,
              color: "#111",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              marginBottom: 4,
            }}
          >
            {expense.vendor || "Unknown Vendor"}
          </div>
          <div style={{ fontSize: 12, color: "#888", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
            {(isCredit || isRefund) && (
              <span style={{
                fontSize: 9, fontWeight: 800, color: "#15803d",
                background: "#DCFCE7", borderRadius: 4, padding: "1px 5px",
                letterSpacing: 0.3, textTransform: "uppercase",
              }}>
                {isRefund ? 'Refund' : 'Credit'}
              </span>
            )}
            {expense.reimbursed && (
              <span title="Reimbursed" style={{
                fontSize: 9, fontWeight: 800, color: '#15803d',
                background: '#DCFCE7', borderRadius: 4, padding: '1px 5px',
                letterSpacing: 0.3, textTransform: 'uppercase',
              }}>
                Reimbursed
              </span>
            )}
            {isPaired && (
              <span title={`Linked to ${expense.pairedWithFile}`} style={{ display: "flex", alignItems: "center" }}>
                <Link2 size={11} color={T.tealDeep} />
              </span>
            )}
            {expense.location ? expense.location + " · " : ""}{expense.category?.replace(/^[^\w]+/, "")} · {formatDate(expense.date)}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: (isCredit || isRefund) ? "#15803d" : "#111", ...amountStyle() }}>
            {isCredit && !isRefund ? "+" : ""}{formatAmount(expense.total, expense.currency)}
          </div>
          {expense.convertedTotal != null && expense.convertedCurrency !== expense.currency && (
            <div style={{ fontSize: 11, color: "#6B7280", marginTop: 1, ...amountStyle() }}>
              {isCredit && !isRefund ? "+" : ""}{formatAmount(expense.convertedTotal, expense.convertedCurrency)}
            </div>
          )}
          {expense.tip > 0 && (
            <div style={{ fontSize: 11, color: T.tealDeep, ...amountStyle() }}>
              +{formatAmount(expense.tip, expense.currency)} tip
            </div>
          )}
        </div>
      </div>
      {onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); setConfirmDelete(true); }}
          title="Delete expense"
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "#ccc", padding: "4px 12px 4px 0",
            alignSelf: "stretch", display: "flex", alignItems: "center",
            transition: "color 0.15s", flexShrink: 0,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#EF4444")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#ccc")}
        >
          <Trash2 size={15} />
        </button>
      )}
      {confirmDelete && (
        <DeleteExpenseModal
          vendorName={expense.vendor || 'this expense'}
          onConfirm={() => { setConfirmDelete(false); onDelete(expense.id); }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

export default ExpenseItem;
